import { MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { deleteLoanProgram, getLoanPrograms } from 'services'
import { svgLoading } from 'stories/assets'
import { Button, Input2 } from 'stories/components'
import { confirm } from 'utils'

import LoanProgramForm from './LoanProgramForm'
import type { ILoanProgram } from './types'

export function LoanPrograms() {
  const [query, setQuery] = useState('')
  const [total, setTotal] = useState(0)
  const [loanPrograms, setLoanPrograms] = useState<ILoanProgram[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const [selectedIndex, setSelectedIndex] = useState(-1)

  useEffect(() => {
    refresh()
  }, [])

  const refresh = () => {
    setIsLoading(true)

    getLoanPrograms()
      .then(({ data }: { data: ILoanProgram[] }) => {
        setTotal(data.length)
        const sortData = data.sort(function (a, b) {
          return a.programName.localeCompare(b.programName)
        })
        setLoanPrograms(sortData)
      })
      .finally(() => setIsLoading(false))
  }

  if (isEditing)
    return (
      <LoanProgramForm
        lastUpdatedAt={lastUpdatedAt}
        defaults={selectedIndex == -1 ? null : loanPrograms[selectedIndex]}
        onBack={() => setIsEditing(false)}
        onComplete={refresh}
      />
    )

  const onAdd = () => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(-1)
    setIsEditing(true)
  }

  const onEdit = (index: number) => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(index)
    setIsEditing(true)
  }

  const onTrash = async (index: number) => {
    const { id, no } = loanPrograms[index]
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to remove this Loan Program?
        <br />
        <span className="text-gray-600">Loan Program No: {no}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setIsLoading(true)

    deleteLoanProgram(id)
      .then(() => {
        refresh()
        toast('Successfully deleted!', { type: 'info' })
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <div className="LoanPrograms-container">
      <h2 className="text-xl font-bold flex items-center mb-3">
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>

      <div className="flex flex-wrap justify-between mb-3">
        <div className="flex items-center flex-wrap">
          <div className="md:w-96 w-72">
            <Input2
              type="search"
              title="Search"
              hasIcon
              icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
              value={query}
              onChange={(value) => setQuery(value)}
            />
          </div>
          <p className="ml-5 text-[15px]">- {total} Loan Programs</p>
        </div>
        <div className="w-32">
          <Button full onClick={onAdd}>
            Add
          </Button>
        </div>
      </div>

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                No
              </th>
              <th scope="col" className="px-3 py-3">
                Program Code
              </th>
              <th scope="col" className="px-3 py-3">
                Product Type
              </th>
              <th scope="col" className="px-3 py-3">
                Program Name
              </th>
              <th scope="col" className="px-3 py-3">
                Mortgage Type
              </th>
              <th scope="col" className="px-3 py-3">
                Amortization Type
              </th>
              <th scope="col" className="px-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="">
            {loanPrograms.map((program, index) => {
              const { no, programCode, productName, programName, mortgageType, amortizationType } = program
              const conditionTexts = [no, programCode, productName, programName, mortgageType, amortizationType]
                .join(' ')
                .toLowerCase()
              if (query && !conditionTexts.includes(query.trim().toLowerCase())) return null

              return (
                <tr className={`border-b ${index % 2 ? 'bg-slate-50' : 'bg-white'}`} key={`${index}`}>
                  <td scope="row" className="px-3 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    {no}
                  </td>
                  <td className="px-3">{programCode}</td>
                  <td className="px-3">{productName}</td>
                  <td className="px-3">{programName}</td>
                  <td className="px-3">{mortgageType}</td>
                  <td className="px-3">{amortizationType}</td>
                  <td className="">
                    <span className="flex px-3">
                      <span className="text-shade-blue p-1 hover-shadow1 cursor-pointer" onClick={() => onEdit(index)}>
                        <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                      </span>
                      <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onTrash(index)}>
                        <TrashIcon className="w-4 h-4"></TrashIcon>
                      </span>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
